import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  FONT_CATEGORIES, ALL_FONT_FAMILIES, loadAllHeroFonts,
  TEXT_SHADOW_PRESETS, COLOR_PRESETS, DEFAULT_HERO_SETTINGS,
} from './heroFonts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL && BACKEND_URL.trim() ? `${BACKEND_URL}/api` : '/api';

const generateId = () => `l-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

// ===== DEFAULT LAYER FACTORY =====
const createDefaultLayer = (type) => {
  const id = generateId();
  const base = { id, type, x: 50, y: 50, width: 'auto', height: 'auto', rotation: 0, zIndex: 10, opacity: 100, visible: true, style: {} };

  switch (type) {
    case 'heading':
      return { ...base, content: 'Heading Text', zIndex: 20, style: {
        fontFamily: 'Cinzel Decorative', fontSize: '56px', fontWeight: '400',
        color: '#D4B876', textAlign: 'center',
        textShadow: '0 0 6px rgba(212,184,118,0.5), 0 0 18px rgba(200,162,74,0.3), 0 2px 4px rgba(0,0,0,0.4)',
        letterSpacing: '0.04em', lineHeight: '1.1',
      }};
    case 'text':
      return { ...base, content: 'Your text here', zIndex: 10, style: {
        fontFamily: 'Cormorant Garamond', fontSize: '18px', fontWeight: '400',
        color: 'rgba(212,184,118,0.85)', textAlign: 'center',
        letterSpacing: '0.02em', lineHeight: '1.6',
      }};
    case 'button':
      return { ...base, content: 'Click Here', zIndex: 25, style: {
        fontFamily: 'Cinzel', fontSize: '12px', fontWeight: '400',
        color: '#FAF7F2', backgroundColor: 'rgba(196,168,118,0.8)',
        textAlign: 'center', letterSpacing: '0.15em', textTransform: 'uppercase',
        padding: '14px 36px', borderRadius: '4px', link: '/membership',
      }};
    case 'image':
      return { ...base, content: '', width: '200px', zIndex: 8, style: { objectFit: 'contain', borderRadius: '0px' }};
    case 'decorative':
      return { ...base, content: '', width: '100px', zIndex: 5, style: { objectFit: 'contain' }};
    default:
      return base;
  }
};

// ===== CONVERT LEGACY CONTENT =====
const convertLegacy = (content) => {
  const layers = [];
  if (content.welcome_text) {
    layers.push({ ...createDefaultLayer('text'), content: content.welcome_text, x: 50, y: 28, style: {
      fontFamily: 'Cormorant Garamond', fontSize: '12px', fontWeight: '400',
      color: 'rgba(212,184,118,0.8)', textAlign: 'center', letterSpacing: '0.4em', textTransform: 'uppercase',
    }});
  }
  if (content.main_title) {
    layers.push({ ...createDefaultLayer('heading'), content: content.main_title, x: 50, y: 40, style: {
      fontFamily: 'Cinzel Decorative', fontSize: '72px', fontWeight: '400',
      color: '#D4B876', textAlign: 'center',
      textShadow: '0 0 6px rgba(212,184,118,0.5), 0 0 18px rgba(200,162,74,0.3), 0 2px 4px rgba(0,0,0,0.4)',
      letterSpacing: '0.04em', lineHeight: '1.1',
    }});
  }
  if (content.subtitle) {
    layers.push({ ...createDefaultLayer('text'), content: content.subtitle, x: 50, y: 52, style: {
      fontFamily: 'Cormorant Garamond', fontSize: '14px', fontWeight: '400',
      color: 'rgba(212,184,118,0.8)', textAlign: 'center', letterSpacing: '0.2em',
    }});
  }
  if (content.description) {
    layers.push({ ...createDefaultLayer('text'), content: content.description, x: 50, y: 60, width: '480px', style: {
      fontFamily: 'Cormorant Garamond', fontSize: '16px', fontWeight: '400',
      color: 'rgba(212,184,118,0.85)', textAlign: 'center', lineHeight: '1.6',
    }});
  }
  if (content.cta_text) {
    layers.push({ ...createDefaultLayer('button'), content: content.cta_text, x: 50, y: 73, style: {
      fontFamily: 'Cinzel', fontSize: '12px', fontWeight: '400',
      color: '#FAF7F2', backgroundColor: 'rgba(196,168,118,0.8)',
      textAlign: 'center', letterSpacing: '0.15em', textTransform: 'uppercase',
      padding: '14px 36px', borderRadius: '4px', link: content.cta_link || '/membership',
    }});
  }
  // Use existing background or the default hero background
  const bgImage = content.background_image || 'https://customer-assets.emergentagent.com/job_cinematic-reveal-1/artifacts/f4hqb2ww_FD567340-8D98-47C1-A961-49662CE54C8B.png';
  return {
    settings: { ...DEFAULT_HERO_SETTINGS, background_image: bgImage },
    layers,
  };
};

// ===== TEMPLATES =====
const HERO_TEMPLATES = [
  {
    name: 'Classic Centered Gold',
    data: {
      settings: { ...DEFAULT_HERO_SETTINGS, background_overlay_opacity: 40 },
      layers: [
        { ...createDefaultLayer('text'), id: 'tc1', content: 'WELCOME TO', x: 50, y: 30, style: { fontFamily: 'Cormorant Garamond', fontSize: '13px', fontWeight: '400', color: 'rgba(212,184,118,0.8)', textAlign: 'center', letterSpacing: '0.4em', textTransform: 'uppercase' } },
        { ...createDefaultLayer('heading'), id: 'tc2', content: 'The Timeless Post', x: 50, y: 43, style: { fontFamily: 'Cinzel Decorative', fontSize: '64px', fontWeight: '400', color: '#D4B876', textAlign: 'center', textShadow: '0 0 6px rgba(212,184,118,0.5), 0 0 18px rgba(200,162,74,0.3), 0 2px 4px rgba(0,0,0,0.4)', letterSpacing: '0.04em', lineHeight: '1.1' } },
        { ...createDefaultLayer('text'), id: 'tc3', content: 'An Immersive Storytelling Experience', x: 50, y: 56, style: { fontFamily: 'Cormorant Garamond', fontSize: '16px', fontWeight: '400', color: 'rgba(212,184,118,0.8)', textAlign: 'center', letterSpacing: '0.15em' } },
        { ...createDefaultLayer('button'), id: 'tc4', content: 'Begin Your Journey', x: 50, y: 70, style: { fontFamily: 'Cinzel', fontSize: '12px', fontWeight: '400', color: '#FAF7F2', backgroundColor: 'rgba(196,168,118,0.8)', textAlign: 'center', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '14px 36px', borderRadius: '4px', link: '/membership' } },
      ]
    }
  },
  {
    name: 'Dramatic Cinematic',
    data: {
      settings: { ...DEFAULT_HERO_SETTINGS, background_overlay_opacity: 50 },
      layers: [
        { ...createDefaultLayer('heading'), id: 'td1', content: 'THE PROPHECY UNFOLDS', x: 50, y: 45, style: { fontFamily: 'Cinzel', fontSize: '48px', fontWeight: '700', color: '#FFFFFF', textAlign: 'center', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.4)', letterSpacing: '0.12em', lineHeight: '1.2' } },
        { ...createDefaultLayer('text'), id: 'td2', content: 'Letters from beyond the veil of time', x: 50, y: 58, style: { fontFamily: 'Cormorant Garamond', fontSize: '20px', fontWeight: '300', color: 'rgba(255,255,255,0.75)', textAlign: 'center', letterSpacing: '0.08em', lineHeight: '1.6' } },
      ]
    }
  },
  {
    name: 'Editorial Left-Aligned',
    data: {
      settings: { ...DEFAULT_HERO_SETTINGS, background_overlay_opacity: 35 },
      layers: [
        { ...createDefaultLayer('text'), id: 'te1', content: 'CHAPTER ONE', x: 18, y: 35, style: { fontFamily: 'Cormorant Garamond', fontSize: '12px', fontWeight: '500', color: 'rgba(212,184,118,0.7)', textAlign: 'left', letterSpacing: '0.3em', textTransform: 'uppercase' } },
        { ...createDefaultLayer('heading'), id: 'te2', content: 'The First\nLetter Arrives', x: 18, y: 48, style: { fontFamily: 'Playfair Display', fontSize: '52px', fontWeight: '400', color: '#D4B876', textAlign: 'left', textShadow: '0 2px 8px rgba(0,0,0,0.6)', letterSpacing: '0.02em', lineHeight: '1.15', whiteSpace: 'pre-line' } },
        { ...createDefaultLayer('button'), id: 'te3', content: 'Subscribe Now', x: 18, y: 66, style: { fontFamily: 'Cinzel', fontSize: '11px', fontWeight: '400', color: '#FAF7F2', backgroundColor: '#B09060', textAlign: 'center', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '12px 32px', borderRadius: '2px', link: '/membership' } },
      ]
    }
  },
  {
    name: 'Minimal Elegant',
    data: {
      settings: { ...DEFAULT_HERO_SETTINGS, height_desktop: '80vh', height_tablet: '70vh', height_mobile: '60vh', background_overlay_opacity: 25 },
      layers: [
        { ...createDefaultLayer('heading'), id: 'tm1', content: 'The Timeless Post', x: 50, y: 48, style: { fontFamily: 'EB Garamond', fontSize: '42px', fontWeight: '400', color: '#D4BA8A', textAlign: 'center', letterSpacing: '0.08em', lineHeight: '1.2' } },
        { ...createDefaultLayer('text'), id: 'tm2', content: 'Immersive storytelling, delivered to your door', x: 50, y: 60, style: { fontFamily: 'Cormorant Garamond', fontSize: '16px', fontWeight: '300', color: 'rgba(212,184,118,0.7)', textAlign: 'center', letterSpacing: '0.05em' } },
      ]
    }
  },
  {
    name: 'Story Opening',
    data: {
      settings: { ...DEFAULT_HERO_SETTINGS, background_overlay_opacity: 45 },
      layers: [
        { ...createDefaultLayer('decorative'), id: 'ts1', content: '', x: 50, y: 22, width: '60px', style: { objectFit: 'contain' } },
        { ...createDefaultLayer('text'), id: 'ts2', content: '"In the year of the golden dawn,\nwhen ink flows like rivers..."', x: 50, y: 40, width: '500px', style: { fontFamily: 'Cormorant Garamond', fontSize: '22px', fontWeight: '300', color: 'rgba(212,184,118,0.9)', textAlign: 'center', lineHeight: '1.8', fontStyle: 'italic', whiteSpace: 'pre-line' } },
        { ...createDefaultLayer('text'), id: 'ts3', content: '- The First Binding', x: 50, y: 58, style: { fontFamily: 'Cormorant Garamond', fontSize: '14px', fontWeight: '400', color: 'rgba(212,184,118,0.6)', textAlign: 'center', letterSpacing: '0.1em' } },
        { ...createDefaultLayer('button'), id: 'ts4', content: 'Enter the Story', x: 50, y: 72, style: { fontFamily: 'Cinzel', fontSize: '11px', fontWeight: '400', color: '#FAF7F2', backgroundColor: 'rgba(176,144,96,0.7)', textAlign: 'center', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '12px 32px', borderRadius: '4px', border: '1px solid rgba(212,184,118,0.3)', link: '/membership' } },
      ]
    }
  },
];

// ===== ICON COMPONENTS =====
const LayerTypeIcon = ({ type, size = 16 }) => {
  const s = { width: size, height: size, display: 'inline-block', textAlign: 'center', fontSize: size * 0.7, lineHeight: `${size}px` };
  switch (type) {
    case 'heading': return <span style={s} className="text-amber-400 font-bold">H</span>;
    case 'text': return <span style={s} className="text-blue-400 font-serif">T</span>;
    case 'button': return <span style={s} className="text-green-400">[B]</span>;
    case 'image': return <span style={s} className="text-purple-400">&#9638;</span>;
    case 'decorative': return <span style={s} className="text-pink-400">&#10022;</span>;
    default: return <span style={s} className="text-gray-400">?</span>;
  }
};

// ===== COLLAPSIBLE SECTION =====
const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-700 last:border-b-0">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-300 hover:text-white">
        {title}
        <span className="text-gray-500">{open ? '▼' : '▶'}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
};

// ===== SMALL INPUT COMPONENTS =====
const SmallLabel = ({ children }) => <label className="block text-gray-400 text-[11px] mb-0.5">{children}</label>;

const SmallInput = ({ label, value, onChange, type = 'text', ...rest }) => (
  <div>
    <SmallLabel>{label}</SmallLabel>
    <input type={type} value={value ?? ''} onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs" {...rest} />
  </div>
);

const SmallSelect = ({ label, value, onChange, options }) => (
  <div>
    <SmallLabel>{label}</SmallLabel>
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs">
      {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const SmallRange = ({ label, value, onChange, min, max, step = 1 }) => (
  <div>
    <SmallLabel>{label}: {value}</SmallLabel>
    <input type="range" min={min} max={max} step={step} value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full h-1.5 bg-gray-600 rounded appearance-none cursor-pointer" />
  </div>
);

const ColorInput = ({ label, value, onChange }) => {
  const hexValue = value && value.startsWith('#') ? value : '#D4B876';
  return (
    <div>
      <SmallLabel>{label}</SmallLabel>
      <div className="flex gap-1 items-center">
        <input type="color" value={hexValue} onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border border-gray-600 cursor-pointer bg-transparent" />
        <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs" placeholder="#hex or rgba()" />
      </div>
      <div className="flex gap-1 mt-1 flex-wrap">
        {COLOR_PRESETS.slice(0, 8).map(p => (
          <button key={p.value} type="button" title={p.name} onClick={() => onChange(p.value)}
            className="w-4 h-4 rounded border border-gray-600 hover:ring-1 hover:ring-amber-400"
            style={{ backgroundColor: p.value }} />
        ))}
      </div>
    </div>
  );
};

// ===== FONT FAMILY SELECTOR =====
const FontFamilySelect = ({ value, onChange }) => (
  <div>
    <SmallLabel>Font Family</SmallLabel>
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs">
      <option value="">Default</option>
      {FONT_CATEGORIES.map(cat => (
        <optgroup key={cat.name} label={cat.name}>
          {cat.fonts.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </optgroup>
      ))}
    </select>
  </div>
);

// ===== MAIN HERO BUILDER COMPONENT =====
const HeroBuilder = ({ content, onChange, getAuthHeaders }) => {
  // Initialize from content (new or legacy format)
  const initData = useMemo(() => {
    if (content?.layers && Array.isArray(content.layers)) return content;
    if (content && (content.main_title || content.welcome_text)) return convertLegacy(content);
    return { settings: { ...DEFAULT_HERO_SETTINGS }, layers: [] };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [heroData, setHeroData] = useState(initData);
  const [selectedId, setSelectedId] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const canvasRef = useRef(null);

  // Load all fonts for the builder
  useEffect(() => { loadAllHeroFonts(); }, []);

  // Sync to parent
  useEffect(() => { onChange(heroData); }, [heroData]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedLayer = heroData.layers.find(l => l.id === selectedId);

  // ===== LAYER MANAGEMENT =====
  const updateSettings = (key, value) => {
    setHeroData(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
  };

  const addLayer = (type) => {
    const layer = createDefaultLayer(type);
    setHeroData(prev => ({ ...prev, layers: [...prev.layers, layer] }));
    setSelectedId(layer.id);
    setShowAddMenu(false);
  };

  const updateLayer = useCallback((id, updates) => {
    setHeroData(prev => ({
      ...prev,
      layers: prev.layers.map(l => l.id === id ? { ...l, ...updates } : l),
    }));
  }, []);

  const updateLayerStyle = (id, key, value) => {
    setHeroData(prev => ({
      ...prev,
      layers: prev.layers.map(l => l.id === id ? { ...l, style: { ...l.style, [key]: value } } : l),
    }));
  };

  const removeLayer = (id) => {
    if (!window.confirm('Delete this layer?')) return;
    setHeroData(prev => ({ ...prev, layers: prev.layers.filter(l => l.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateLayer = (id) => {
    const source = heroData.layers.find(l => l.id === id);
    if (!source) return;
    const dup = { ...JSON.parse(JSON.stringify(source)), id: generateId(), x: Math.min(source.x + 3, 95), y: Math.min(source.y + 3, 95) };
    setHeroData(prev => ({ ...prev, layers: [...prev.layers, dup] }));
    setSelectedId(dup.id);
  };

  const moveLayerZ = (id, direction) => {
    const layer = heroData.layers.find(l => l.id === id);
    if (!layer) return;
    const newZ = direction === 'up' ? (layer.zIndex || 0) + 1 : Math.max(0, (layer.zIndex || 0) - 1);
    updateLayer(id, { zIndex: newZ });
  };

  // ===== DRAG HANDLERS =====
  const handleLayerMouseDown = (e, layerId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(layerId);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const layer = heroData.layers.find(l => l.id === layerId);
    if (!layer) return;
    setDragging({
      layerId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startLayerX: layer.x,
      startLayerY: layer.y,
      canvasWidth: rect.width,
      canvasHeight: rect.height,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const dx = ((e.clientX - dragging.startMouseX) / dragging.canvasWidth) * 100;
    const dy = ((e.clientY - dragging.startMouseY) / dragging.canvasHeight) * 100;
    const newX = Math.max(0, Math.min(100, dragging.startLayerX + dx));
    const newY = Math.max(0, Math.min(100, dragging.startLayerY + dy));
    updateLayer(dragging.layerId, { x: Math.round(newX * 10) / 10, y: Math.round(newY * 10) / 10 });
  }, [dragging, updateLayer]);

  const handleMouseUp = useCallback(() => { setDragging(null); }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  // ===== IMAGE UPLOAD =====
  const handleImageUpload = async (layerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/upload/media`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      updateLayer(layerId, { content: res.data.url });
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Image upload failed');
    }
  };

  // ===== IMPORT / EXPORT =====
  const handleExport = () => {
    const blob = new Blob([JSON.stringify(heroData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hero-layout.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.settings && Array.isArray(data.layers)) {
          // Validate layers have required fields
          const valid = data.layers.every(l => l.id && l.type && typeof l.x === 'number' && typeof l.y === 'number');
          if (!valid) { alert('Invalid layer data in file'); return; }
          setHeroData(data);
          setSelectedId(null);
        } else {
          alert('Invalid hero layout file. Must contain "settings" and "layers" fields.');
        }
      } catch { alert('Failed to parse JSON file'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const applyTemplate = (template) => {
    if (!window.confirm(`Apply "${template.name}" template? This will replace your current layers.`)) return;
    const data = JSON.parse(JSON.stringify(template.data));
    // Preserve current background if set
    if (heroData.settings.background_image) {
      data.settings.background_image = heroData.settings.background_image;
    }
    // Give new IDs to avoid conflicts
    data.layers = data.layers.map(l => ({ ...l, id: generateId() }));
    setHeroData(data);
    setSelectedId(null);
    setShowTemplates(false);
  };

  // ===== RENDER: CANVAS LAYER =====
  const renderCanvasLayer = (layer) => {
    const isSelected = selectedId === layer.id;
    const isDraggingThis = dragging?.layerId === layer.id;
    const s = layer.style || {};

    const wrapStyle = {
      position: 'absolute',
      left: `${layer.x}%`,
      top: `${layer.y}%`,
      transform: `translate(-50%, -50%) rotate(${layer.rotation || 0}deg)`,
      zIndex: layer.zIndex || 1,
      opacity: layer.visible === false ? 0.25 : (layer.opacity ?? 100) / 100,
      cursor: isDraggingThis ? 'grabbing' : 'grab',
      outline: isSelected ? '2px solid #f59e0b' : '1px dashed transparent',
      outlineOffset: '3px',
      maxWidth: '90%',
      userSelect: 'none',
    };

    if (layer.width && layer.width !== 'auto') wrapStyle.width = layer.width;
    if (layer.height && layer.height !== 'auto') wrapStyle.height = layer.height;

    const textStyle = {
      fontFamily: s.fontFamily || 'inherit',
      fontSize: s.fontSize || '16px',
      fontWeight: s.fontWeight || '400',
      color: s.color || '#D4B876',
      textAlign: s.textAlign || 'center',
      textShadow: s.textShadow === 'none' ? undefined : s.textShadow,
      letterSpacing: s.letterSpacing || 'normal',
      lineHeight: s.lineHeight || '1.4',
      textTransform: s.textTransform || 'none',
      whiteSpace: s.whiteSpace || (layer.width && layer.width !== 'auto' ? 'normal' : 'nowrap'),
      fontStyle: s.fontStyle || 'normal',
      margin: 0,
    };

    const renderContent = () => {
      switch (layer.type) {
        case 'heading':
        case 'text':
          return <div style={textStyle}>{layer.content || '(empty)'}</div>;
        case 'button':
          return (
            <div style={{
              ...textStyle,
              backgroundColor: s.backgroundColor || 'rgba(196,168,118,0.8)',
              padding: s.padding || '14px 36px',
              borderRadius: s.borderRadius || '4px',
              border: s.border || 'none',
              display: 'inline-block',
            }}>
              {layer.content || 'Button'}
            </div>
          );
        case 'image':
        case 'decorative':
          return layer.content ? (
            <img src={layer.content} alt="" style={{
              width: '100%', height: layer.height && layer.height !== 'auto' ? layer.height : 'auto',
              objectFit: s.objectFit || 'contain', borderRadius: s.borderRadius || '0px',
              pointerEvents: 'none',
            }} />
          ) : (
            <div className="flex items-center justify-center bg-gray-700/50 border border-dashed border-gray-500 rounded text-gray-400 text-xs"
              style={{ width: layer.width || '120px', height: '60px' }}>
              {layer.type === 'image' ? 'Image' : 'Stamp'}
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div
        key={layer.id}
        style={wrapStyle}
        onMouseDown={(e) => handleLayerMouseDown(e, layer.id)}
        onClick={(e) => e.stopPropagation()}
        data-testid={`canvas-layer-${layer.id}`}
      >
        {renderContent()}
      </div>
    );
  };

  // ===== RENDER: PROPERTIES PANEL =====
  const renderProperties = () => {
    if (!selectedLayer) {
      return (
        <div className="space-y-0">
          <Section title="Background" defaultOpen={true}>
            <SmallInput label="Image URL" value={heroData.settings.background_image} onChange={(v) => updateSettings('background_image', v)} placeholder="Paste URL or upload" />
            <div>
              <label className="inline-block bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-xs cursor-pointer mt-1">
                Upload Image
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData(); fd.append('file', file);
                  try {
                    const token = localStorage.getItem('admin_token');
                    const res = await axios.post(`${API}/admin/upload/media`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                    updateSettings('background_image', res.data.url);
                  } catch { alert('Upload failed'); }
                }} />
              </label>
            </div>
            {heroData.settings.background_image && (
              <img src={heroData.settings.background_image} alt="BG Preview" className="w-full h-20 object-cover rounded mt-1" />
            )}
            <SmallInput label="Video URL" value={heroData.settings.background_video} onChange={(v) => updateSettings('background_video', v)} placeholder="MP4 URL" />
            <SmallSelect label="Position" value={heroData.settings.background_position} onChange={(v) => updateSettings('background_position', v)}
              options={['center center', 'center top', 'center bottom', 'left center', 'right center', 'center 20%', 'center 30%', 'center 40%']} />
            <ColorInput label="Overlay Color" value={heroData.settings.background_overlay_color} onChange={(v) => updateSettings('background_overlay_color', v)} />
            <SmallRange label="Overlay Opacity" value={heroData.settings.background_overlay_opacity ?? 30} onChange={(v) => updateSettings('background_overlay_opacity', v)} min={0} max={100} />
            <SmallRange label="Blur" value={heroData.settings.background_blur ?? 0} onChange={(v) => updateSettings('background_blur', v)} min={0} max={20} />
            <div className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={heroData.settings.background_parallax || false}
                onChange={(e) => updateSettings('background_parallax', e.target.checked)} className="rounded bg-gray-700" />
              <span className="text-gray-400 text-xs">Parallax effect</span>
            </div>
          </Section>
          <Section title="Section Height" defaultOpen={true}>
            <SmallInput label="Desktop" value={heroData.settings.height_desktop} onChange={(v) => updateSettings('height_desktop', v)} placeholder="100vh" />
            <SmallInput label="Tablet" value={heroData.settings.height_tablet} onChange={(v) => updateSettings('height_tablet', v)} placeholder="80vh" />
            <SmallInput label="Mobile" value={heroData.settings.height_mobile} onChange={(v) => updateSettings('height_mobile', v)} placeholder="70vh" />
          </Section>
        </div>
      );
    }

    const sl = selectedLayer;
    const s = sl.style || {};
    const isTextType = sl.type === 'heading' || sl.type === 'text';
    const isButton = sl.type === 'button';
    const isImage = sl.type === 'image' || sl.type === 'decorative';
    const setStyle = (key, val) => updateLayerStyle(sl.id, key, val);

    return (
      <div className="space-y-0" data-testid="layer-properties-panel">
        {/* Content */}
        <Section title="Content" defaultOpen={true}>
          {(isTextType || isButton) && (
            <div>
              <SmallLabel>{sl.type === 'heading' ? 'Heading Text' : sl.type === 'button' ? 'Button Label' : 'Text Content'}</SmallLabel>
              <textarea value={sl.content || ''} onChange={(e) => updateLayer(sl.id, { content: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs h-16 resize-y" />
            </div>
          )}
          {isImage && (
            <>
              <SmallInput label="Image URL" value={sl.content} onChange={(v) => updateLayer(sl.id, { content: v })} placeholder="Paste URL" />
              <label className="inline-block bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-xs cursor-pointer mt-1">
                Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  if (e.target.files[0]) handleImageUpload(sl.id, e.target.files[0]);
                }} />
              </label>
              {sl.content && <img src={sl.content} alt="Preview" className="w-full h-16 object-contain rounded mt-1 bg-gray-900" />}
            </>
          )}
          {isButton && (
            <SmallInput label="Link URL" value={s.link} onChange={(v) => setStyle('link', v)} placeholder="/membership" />
          )}
        </Section>

        {/* Position & Size */}
        <Section title="Position & Size" defaultOpen={true}>
          <div className="grid grid-cols-2 gap-2">
            <SmallInput label="X (%)" value={sl.x} onChange={(v) => updateLayer(sl.id, { x: parseFloat(v) || 0 })} type="number" step="0.5" min="0" max="100" />
            <SmallInput label="Y (%)" value={sl.y} onChange={(v) => updateLayer(sl.id, { y: parseFloat(v) || 0 })} type="number" step="0.5" min="0" max="100" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SmallInput label="Width" value={sl.width} onChange={(v) => updateLayer(sl.id, { width: v })} placeholder="auto or 300px" />
            <SmallInput label="Height" value={sl.height} onChange={(v) => updateLayer(sl.id, { height: v })} placeholder="auto" />
          </div>
          <SmallRange label="Rotation" value={sl.rotation ?? 0} onChange={(v) => updateLayer(sl.id, { rotation: v })} min={-180} max={180} />
          <div className="grid grid-cols-2 gap-2">
            <SmallInput label="Z-Index" value={sl.zIndex} onChange={(v) => updateLayer(sl.id, { zIndex: parseInt(v) || 0 })} type="number" />
            <SmallRange label="Opacity" value={sl.opacity ?? 100} onChange={(v) => updateLayer(sl.id, { opacity: v })} min={0} max={100} />
          </div>
        </Section>

        {/* Typography */}
        {(isTextType || isButton) && (
          <Section title="Typography" defaultOpen={true}>
            <FontFamilySelect value={s.fontFamily} onChange={(v) => setStyle('fontFamily', v)} />
            <div className="grid grid-cols-2 gap-2">
              <SmallInput label="Font Size" value={s.fontSize} onChange={(v) => setStyle('fontSize', v)} placeholder="18px" />
              <SmallSelect label="Weight" value={s.fontWeight} onChange={(v) => setStyle('fontWeight', v)}
                options={[{ value: '300', label: 'Light' }, { value: '400', label: 'Regular' }, { value: '500', label: 'Medium' }, { value: '600', label: 'Semi Bold' }, { value: '700', label: 'Bold' }]} />
            </div>
            <ColorInput label="Color" value={s.color} onChange={(v) => setStyle('color', v)} />
            <SmallSelect label="Text Align" value={s.textAlign} onChange={(v) => setStyle('textAlign', v)}
              options={['left', 'center', 'right']} />
            <div className="grid grid-cols-2 gap-2">
              <SmallInput label="Letter Spacing" value={s.letterSpacing} onChange={(v) => setStyle('letterSpacing', v)} placeholder="0.02em" />
              <SmallInput label="Line Height" value={s.lineHeight} onChange={(v) => setStyle('lineHeight', v)} placeholder="1.4" />
            </div>
            <SmallSelect label="Transform" value={s.textTransform} onChange={(v) => setStyle('textTransform', v)}
              options={['none', 'uppercase', 'lowercase', 'capitalize']} />
            <SmallSelect label="Font Style" value={s.fontStyle || 'normal'} onChange={(v) => setStyle('fontStyle', v)}
              options={['normal', 'italic']} />
            <div>
              <SmallLabel>Text Shadow</SmallLabel>
              <select value={TEXT_SHADOW_PRESETS.find(p => p.value === s.textShadow) ? s.textShadow : 'custom'}
                onChange={(e) => { if (e.target.value !== 'custom') setStyle('textShadow', e.target.value); }}
                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs">
                {TEXT_SHADOW_PRESETS.map(p => <option key={p.name} value={p.value}>{p.name}</option>)}
                <option value="custom">Custom</option>
              </select>
              {!TEXT_SHADOW_PRESETS.find(p => p.value === s.textShadow) && s.textShadow && (
                <input type="text" value={s.textShadow || ''} onChange={(e) => setStyle('textShadow', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs mt-1" placeholder="Custom CSS text-shadow" />
              )}
            </div>
          </Section>
        )}

        {/* Button Styling */}
        {isButton && (
          <Section title="Button Style" defaultOpen={true}>
            <ColorInput label="Background" value={s.backgroundColor} onChange={(v) => setStyle('backgroundColor', v)} />
            <SmallInput label="Padding" value={s.padding} onChange={(v) => setStyle('padding', v)} placeholder="14px 36px" />
            <SmallInput label="Border Radius" value={s.borderRadius} onChange={(v) => setStyle('borderRadius', v)} placeholder="4px" />
            <SmallInput label="Border" value={s.border} onChange={(v) => setStyle('border', v)} placeholder="1px solid rgba(212,184,118,0.3)" />
          </Section>
        )}

        {/* Image Settings */}
        {isImage && (
          <Section title="Image Style" defaultOpen={true}>
            <SmallSelect label="Object Fit" value={s.objectFit} onChange={(v) => setStyle('objectFit', v)}
              options={['contain', 'cover', 'fill', 'none']} />
            <SmallInput label="Border Radius" value={s.borderRadius} onChange={(v) => setStyle('borderRadius', v)} placeholder="0px" />
          </Section>
        )}

        {/* Actions */}
        <div className="px-3 py-2 flex gap-2 flex-wrap">
          <button type="button" onClick={() => duplicateLayer(sl.id)} className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded">Duplicate</button>
          <button type="button" onClick={() => moveLayerZ(sl.id, 'up')} className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded">Z +1</button>
          <button type="button" onClick={() => moveLayerZ(sl.id, 'down')} className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded">Z -1</button>
          <button type="button" onClick={() => removeLayer(sl.id)} className="text-xs bg-red-900 hover:bg-red-800 text-red-300 px-2 py-1 rounded" data-testid="delete-layer-btn">Delete</button>
        </div>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div className="hero-builder -mx-6 -mb-4" data-testid="hero-builder">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 border-b border-gray-700 flex-wrap" data-testid="hero-builder-toolbar">
        {/* Add Layer */}
        <div className="relative">
          <button type="button" onClick={() => setShowAddMenu(!showAddMenu)}
            className="bg-amber-600 hover:bg-amber-500 text-white px-3 py-1.5 rounded text-xs font-medium"
            data-testid="add-layer-btn">
            + Add Layer
          </button>
          {showAddMenu && (
            <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg z-50 min-w-[160px]">
              {['heading', 'text', 'button', 'image', 'decorative'].map(type => (
                <button key={type} type="button" onClick={() => addLayer(type)}
                  className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2"
                  data-testid={`add-layer-${type}`}>
                  <LayerTypeIcon type={type} size={14} />
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Templates */}
        <div className="relative">
          <button type="button" onClick={() => setShowTemplates(!showTemplates)}
            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded text-xs"
            data-testid="templates-btn">
            Templates
          </button>
          {showTemplates && (
            <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg z-50 min-w-[200px]">
              {HERO_TEMPLATES.map((t, i) => (
                <button key={i} type="button" onClick={() => applyTemplate(t)}
                  className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-700 hover:text-white">
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Import/Export */}
        <button type="button" onClick={handleExport} className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded text-xs" data-testid="export-btn">
          Export
        </button>
        <label className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded text-xs cursor-pointer" data-testid="import-btn">
          Import
          <input type="file" accept=".json" className="hidden" onChange={handleImport} />
        </label>

        <span className="text-gray-500 text-[10px] ml-auto">
          {heroData.layers.length} layer{heroData.layers.length !== 1 ? 's' : ''} | Click canvas to deselect
        </span>
      </div>

      {/* Main Builder Area */}
      <div className="flex" style={{ height: '55vh', minHeight: '380px' }}>
        {/* Left: Layer Stack */}
        <div className="w-[200px] flex-shrink-0 bg-gray-800 border-r border-gray-700 overflow-y-auto" data-testid="layer-stack-panel">
          <div className="px-2 py-2">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 px-1">Layers</p>
            {heroData.layers.length === 0 && (
              <p className="text-gray-500 text-xs text-center py-4">No layers yet.<br/>Click "+ Add Layer"</p>
            )}
            {[...heroData.layers].reverse().map(layer => (
              <div
                key={layer.id}
                onClick={() => setSelectedId(layer.id)}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded cursor-pointer mb-0.5 text-xs ${
                  selectedId === layer.id ? 'bg-amber-900/40 text-amber-300 ring-1 ring-amber-600/50' : 'text-gray-300 hover:bg-gray-700'
                }`}
                data-testid={`layer-item-${layer.id}`}
              >
                {/* Visibility toggle */}
                <button type="button" onClick={(e) => { e.stopPropagation(); updateLayer(layer.id, { visible: !layer.visible }); }}
                  className={`w-4 flex-shrink-0 ${layer.visible !== false ? 'text-amber-400' : 'text-gray-600'}`}
                  title={layer.visible !== false ? 'Visible' : 'Hidden'}>
                  {layer.visible !== false ? '◉' : '○'}
                </button>
                <LayerTypeIcon type={layer.type} size={13} />
                <span className="truncate flex-1 text-[11px]">{layer.content ? layer.content.substring(0, 20) : `(${layer.type})`}</span>
                <span className="text-gray-500 text-[9px] flex-shrink-0">z{layer.zIndex}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Canvas */}
        <div className="flex-1 bg-gray-900 overflow-hidden flex items-center justify-center p-3" data-testid="canvas-container">
          <div
            ref={canvasRef}
            className="relative w-full h-full overflow-hidden rounded border border-gray-700"
            style={{ background: '#1a1a1a' }}
            onClick={() => setSelectedId(null)}
            data-testid="hero-canvas"
          >
            {/* Background Preview */}
            {heroData.settings.background_image && (
              <img src={heroData.settings.background_image} alt="" className="absolute inset-0 w-full h-full object-cover"
                style={{
                  objectPosition: heroData.settings.background_position || 'center center',
                  filter: heroData.settings.background_blur ? `blur(${heroData.settings.background_blur}px)` : undefined,
                }} />
            )}
            {/* Overlay Preview */}
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundColor: heroData.settings.background_overlay_color || '#000',
              opacity: (heroData.settings.background_overlay_opacity ?? 30) / 100,
            }} />
            {/* Layers */}
            {heroData.layers
              .filter(l => l.visible !== false)
              .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
              .map(renderCanvasLayer)}
            {/* Grid guides when dragging */}
            {dragging && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-amber-500/20" />
                <div className="absolute top-1/2 left-0 right-0 h-px bg-amber-500/20" />
              </div>
            )}
          </div>
        </div>

        {/* Right: Properties */}
        <div className="w-[260px] flex-shrink-0 bg-gray-800 border-l border-gray-700 overflow-y-auto" data-testid="properties-panel">
          <div className="px-2 py-2">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 px-1">
              {selectedLayer ? `${selectedLayer.type} Properties` : 'Section Settings'}
            </p>
            {renderProperties()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroBuilder;
